import React, { forwardRef } from 'react';
import { css } from '@emotion/react';
import { useFocusRing } from '@react-aria/focus';
import { AriaFocusProps } from '../../types/common';
import {
  input_wrapper_base_css,
  input_default_style,
  input_disabled_style,
  input_error_style,
  input_focus_style,
  getInputSizeStyle,
} from '../input/styles';
import classNames from 'classnames';
import { TimePickerIcon, TimePickerIconProps } from './TimePickerIcon';
import { InputBase, InputBaseProps } from '../input/InputBase';
import { IconArrowDirectionRight } from '../icons/generated';
import { semantic_colors } from '../../foundation';
import { useProviderConfig } from '../provider';
import { getTimePickerInputWrapperStyleBySize, range_input_default_style } from './styles';

export interface TimeRangeInputProps
  extends Omit<InputBaseProps, 'leftAddon' | 'rightAddon' | 'alignment'>,
    AriaFocusProps {
  fromTime?: string;
  toTime?: string;
  onClickRemoveButton?: () => void;
}

// TODO(@olive-sally): Date/Time RangeInput 통합
export const TimeRangeInput = forwardRef<HTMLInputElement, TimeRangeInputProps & TimePickerIconProps>(
  (
    {
      fromTime = '',
      toTime = '',
      status = 'info',
      disabled,
      width,
      className,
      size,
      showRemoveIcon,
      unstableFocusVisible,
      onClickRemoveButton,
    },
    ref,
  ) => {
    const { locale } = useProviderConfig();
    const { isFocusVisible, focusProps } = useFocusRing({ within: true, isTextInput: true });
    const status_style = {
      ...input_default_style,
      ...((unstableFocusVisible ?? isFocusVisible) && input_focus_style),
      ...(disabled && input_disabled_style),
      ...(status && status === 'error' && input_error_style),
    };
    const rangeArrowIconSize = size === 'small' ? 12 : 10;

    return (
      <div
        className={classNames('pds-date-range-input', className)}
        css={[
          input_wrapper_base_css,
          getInputSizeStyle(size),
          status_style,
          css`
            width: ${width ?? '100%'};
          `,
          getTimePickerInputWrapperStyleBySize(size),
        ]}
        {...focusProps}
        ref={ref}
      >
        <InputBase
          placeholder={locale?.TimeRangePicker.placeholder[0]}
          value={fromTime}
          readOnly
          style={range_input_default_style}
          disabled={disabled}
          rightIcon={<IconArrowDirectionRight size={rangeArrowIconSize} color={semantic_colors.content.tertiary} />}
          size={size}
        />
        <InputBase
          placeholder={locale?.TimeRangePicker.placeholder[1]}
          value={toTime}
          readOnly
          style={range_input_default_style}
          status={status}
          disabled={disabled}
          rightIcon={<TimePickerIcon showRemoveIcon={showRemoveIcon} onClick={onClickRemoveButton} />}
          size={size}
        />
      </div>
    );
  },
);
